import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PlayCircle } from "@phosphor-icons/react";

/**
 * Reusable "Video Blog" module.
 * Fetches published vlog episodes and renders a compact, magazine-style grid.
 * Variants:
 *   - variant="home"      : featured + 3 cards (for homepage)
 *   - variant="inline"    : 3 cards (for article + category pages)
 *   - variant="sidebar"   : 2 vertical cards (for article sidebar / destination page)
 * Optional filter:
 *   - tag="tokyo"  → pulls only episodes whose tags array contains the tag (case-insensitive)
 */
export default function VideoBlogSection({ variant = "home", tag, limit, heading, eyebrow }) {
  const [items, setItems] = useState([]);
  const cap = limit || (variant === "home" ? 4 : variant === "sidebar" ? 2 : 3);

  useEffect(() => {
    api.get(`/videos?status=published&is_vlog=true&limit=12`).then((r) => {
      let list = r.data.items || [];
      if (tag) {
        const t = tag.toLowerCase();
        list = list.filter((v) => (v.tags || []).some((x) => x.toLowerCase().includes(t)));
        // fallback to unfiltered if empty so section never goes blank
        if (!list.length) list = r.data.items;
      }
      setItems(list.slice(0, cap));
    }).catch(() => setItems([]));
  }, [tag, cap]);

  if (!items.length) return null;

  if (variant === "sidebar") {
    return (
      <aside className="border border-[#D6CFC4] bg-white p-5" data-testid="video-blog-sidebar">
        <div className="overline text-[#B4533C] mb-3">{eyebrow || "Video Blog"}</div>
        <div className="font-serif text-xl leading-tight mb-4">{heading || "On film"}</div>
        <div className="space-y-4">
          {items.map((v) => (
            <Link key={v.id} to={`/video/${v.slug}`} className="group flex gap-3" data-testid={`vlog-sidebar-${v.slug}`}>
              <div className="img-zoom relative w-24 aspect-video overflow-hidden bg-[#EFECE5] border border-[#D6CFC4] shrink-0">
                {v.thumbnail && <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" loading="lazy" />}
                <PlayCircle size={22} weight="thin" className="absolute inset-0 m-auto text-white" />
              </div>
              <div className="min-w-0">
                <div className="overline text-[#B4533C] text-[10px]">EP {v.episode_number || "—"}</div>
                <div className="font-serif text-sm leading-tight mt-0.5 group-hover:text-[#B4533C]">{v.title}</div>
              </div>
            </Link>
          ))}
        </div>
      </aside>
    );
  }

  if (variant === "home") {
    const [featured, ...rest] = items;
    return (
      <section className="container-page py-16 border-t border-[#D6CFC4]" data-testid="video-blog-home">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="overline text-[#B4533C]">{eyebrow || "Video Blog · JetsetLess Travels"}</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2">{heading || "The trips, on film"}</h2>
          </div>
          <Link to="/videos" className="overline text-[#1B3B36] hover:text-[#B4533C] hidden md:inline" data-testid="video-blog-home-all">All episodes →</Link>
        </div>
        <div className="grid md:grid-cols-12 gap-6">
          <Link to={`/video/${featured.slug}`} className="md:col-span-7 group block" data-testid={`vlog-featured-${featured.slug}`}>
            <div className="img-zoom relative aspect-video overflow-hidden bg-[#1C1A17] border border-[#D6CFC4]">
              {featured.thumbnail && <img src={featured.thumbnail} alt={featured.title} className="w-full h-full object-cover" loading="lazy" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <PlayCircle size={64} weight="thin" className="absolute inset-0 m-auto text-white" />
              {featured.duration_seconds && (
                <div className="absolute bottom-3 right-3 bg-black/80 text-white text-xs font-mono px-2 py-1">{fmt(featured.duration_seconds)}</div>
              )}
            </div>
            <div className="overline text-[#B4533C] mt-4">
              {featured.season ? `S${featured.season} · ` : ""}{featured.episode_number ? `Episode ${featured.episode_number}` : "Featured"}
              {featured.location && ` · ${featured.location}`}
            </div>
            <h3 className="font-serif text-2xl md:text-3xl leading-tight mt-2 group-hover:text-[#B4533C]">{featured.title}</h3>
          </Link>
          <div className="md:col-span-5 grid grid-cols-1 gap-5">
            {rest.map((v) => <InlineCard key={v.id} v={v} />)}
          </div>
        </div>
      </section>
    );
  }

  // inline (grid of 3)
  return (
    <section className="my-14" data-testid="video-blog-inline">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline text-[#B4533C]">{eyebrow || "Video Blog"}</div>
          <h3 className="font-serif text-2xl md:text-3xl tracking-tight mt-2">{heading || "Watch the trip"}</h3>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {items.map((v) => <InlineCard key={v.id} v={v} />)}
      </div>
    </section>
  );
}

function InlineCard({ v }) {
  return (
    <Link to={`/video/${v.slug}`} className="group block" data-testid={`vlog-inline-${v.slug}`}>
      <div className="img-zoom relative aspect-video overflow-hidden bg-[#1C1A17] border border-[#D6CFC4]">
        {v.thumbnail && <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" loading="lazy" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <PlayCircle size={40} weight="thin" className="absolute inset-0 m-auto text-white opacity-80" />
        {v.duration_seconds && (
          <div className="absolute bottom-2 right-2 bg-black/80 text-white text-[10px] font-mono px-1.5 py-0.5">{fmt(v.duration_seconds)}</div>
        )}
      </div>
      <div className="overline text-[#B4533C] mt-3 text-[10px]">
        {v.episode_number ? `EP ${v.episode_number}` : "Episode"}
        {v.location && ` · ${v.location}`}
      </div>
      <div className="font-serif text-lg leading-tight mt-1 group-hover:text-[#B4533C]">{v.title}</div>
    </Link>
  );
}

function fmt(s) {
  if (!s) return "";
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}
